import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Vehicle } from '../../../lib/vehicles';

interface StickySidebarProps {
  vehicle: Vehicle;
  monthly: number;
  onAvailability: () => void;
  onPreApproval: () => void;
}

export default function StickySidebar({
  vehicle,
  monthly,
  onAvailability,
  onPreApproval,
}: StickySidebarProps) {
  const [visible, setVisible] = useState(false);
  const [showOacTooltip, setShowOacTooltip] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isSold = vehicle.status === 'sold';
  const isPending = vehicle.status === 'pending';

  // Show sidebar after hero scrolls past
  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      ref={sidebarRef}
      className={`hidden lg:block w-[340px] flex-shrink-0 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <div className="sticky top-24 space-y-4">
        {/* Price Card */}
        <div
          className="rounded-2xl overflow-hidden shadow-xl border border-[#E8E2D6]"
        >
          {/* Header */}
          <div
            className="px-6 py-4 flex items-center gap-3"
            style={{ background: '#1E1A14' }}
          >
            <div className="w-1 h-6 rounded-full bg-[#F5C842]" />
            <span
              className="text-white font-black text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              CLAIM THIS RIDE
            </span>
            {(isSold || isPending) && (
              <span className="ml-auto px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-red-600 text-white">
                {isSold ? 'SOLD' : 'PENDING'}
              </span>
            )}
          </div>

          {/* Price body */}
          <div className="bg-[#FFFBF0] px-6 py-5">
            <p className="text-[10px] font-black text-[#6B7A8D] uppercase tracking-widest mb-1">Asking Price</p>
            <div
              className="text-4xl font-black text-[#1A2332] leading-none mb-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              ${vehicle.price.toLocaleString()}
            </div>
            {monthly > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className="text-lg font-black text-[#D97706]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Est. ${Math.round(monthly).toLocaleString()}
                  <span className="text-sm font-semibold text-[#6B7A8D]">/mo</span>
                </span>
                <span className="text-xs text-[#6B7A8D]">· 60 mo @ 5.9%</span>
                {/* OAC badge + tooltip */}
                <div className="relative flex items-center gap-1">
                  <span className="text-xs font-bold text-[#D97706]">OAC</span>
                  <button
                    type="button"
                    onMouseEnter={() => setShowOacTooltip(true)}
                    onMouseLeave={() => setShowOacTooltip(false)}
                    onFocus={() => setShowOacTooltip(true)}
                    onBlur={() => setShowOacTooltip(false)}
                    aria-label="OAC means On Approved Credit — actual rate depends on your credit history"
                    className="w-4 h-4 flex items-center justify-center rounded-full bg-[#E8E2D6] text-[#374151] hover:bg-[#E8B84B] hover:text-[#1A2332] transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F]"
                  >
                    <i className="ri-information-line text-[10px]" />
                  </button>
                  {showOacTooltip && (
                    <div
                      role="tooltip"
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-[#1A2332] text-white text-xs rounded-lg px-3 py-2 shadow-lg z-50 pointer-events-none"
                    >
                      <strong>OAC</strong> = On Approved Credit. Estimated payment assumes 10% down, 60-month term at 5.9% APR. Actual rate varies by credit history.
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A2332]" />
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-xs text-[#6B7A8D] mt-1.5 flex items-center gap-1">
              <i className="ri-shield-check-line text-green-600" />
              No dealer fees · Fair price guaranteed
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="rounded-2xl overflow-hidden border border-[#E8E2D6] bg-[#FFFBF0] shadow-sm">
          <div className="p-5 space-y-3">
            {isSold ? (
              <Link
                to="/inventory"
                className="w-full py-3.5 flex items-center justify-center gap-2 rounded-xl font-black text-sm text-[#1E1A14] transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
              >
                <i className="ri-car-line" />
                BROWSE SIMILAR CARS
              </Link>
            ) : isPending ? (
              <button
                onClick={onAvailability}
                className="w-full py-3.5 flex items-center justify-center gap-2 rounded-xl font-black text-sm text-[#1E1A14] transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
              >
                <i className="ri-notification-line" />
                JOIN THE WAITLIST
              </button>
            ) : (
              <>
                <button
                  onClick={onAvailability}
                  className="w-full py-3.5 flex items-center justify-center gap-2 rounded-xl font-black text-sm text-white transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                  style={{ background: '#1A2332', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
                >
                  <i className="ri-calendar-check-line" />
                  I WANT THIS CAR
                </button>
                <Link
                  to={`/apply-for-financing?vehicle=${encodeURIComponent(`${vehicle.year} ${vehicle.make} ${vehicle.model}`)}`}
                  className="w-full py-3.5 flex items-center justify-center gap-2 rounded-xl font-black text-sm text-[#1E1A14] transition-all hover:opacity-90 cursor-pointer whitespace-nowrap"
                  style={{ background: '#F5C842', fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
                >
                  <i className="ri-bank-card-line" />
                  GET APPROVED
                </Link>
              </>
            )}

            <a
              href="tel:1-855-468-6348"
              className="w-full py-3 flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-[#1A2332] border-2 border-[#E8E2D6] hover:border-[#F5C842] transition-colors cursor-pointer whitespace-nowrap"
              aria-label="Call Rockhound Auto at 1-855-468-6348"
            >
              <i className="ri-phone-line" />
              1-855-468-6348
            </a>
          </div>
        </div>

        {/* Trust Signals */}
        <div className="rounded-2xl border border-[#E8E2D6] bg-[#FFFBF0] p-5 shadow-sm">
          <ul className="space-y-3">
            {[
              { icon: 'ri-shield-check-line', color: 'text-green-600', title: 'Clean Title Verified', sub: 'History report included' },
              { icon: 'ri-tools-line', color: 'text-[#D97706]', title: 'Safety Inspection', sub: 'Thoroughly checked' },
              { icon: 'ri-truck-line', color: 'text-[#1A2332]', title: 'Free Delivery', sub: 'Within 50 miles' },
              { icon: 'ri-search-eye-line', color: 'text-[#D97706]', title: 'Full Transparency', sub: 'Complete history & disclosure' },
            ].map((b, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border border-[#E8E2D6] flex-shrink-0">
                  <i className={`${b.icon} ${b.color} text-base`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A2332] leading-tight">{b.title}</p>
                  <p className="text-xs text-[#6B7A8D]">{b.sub}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div className="rounded-2xl border border-[#E8E2D6] bg-[#FFFBF0] p-4 shadow-sm">
          <p className="text-[10px] font-black text-[#6B7A8D] uppercase tracking-widest mb-3">Quick Actions</p>
          <nav>
            <ul className="space-y-1.5">
              {[
                { to: '/financing-approval', icon: 'ri-bank-card-line', label: 'Get Approved', sub: 'Fast financing' },
                { to: '/trade-in', icon: 'ri-refresh-line', label: 'Value Your Trade', sub: 'Instant estimate' },
                { to: '/sell-your-car', icon: 'ri-handshake-line', label: 'Sell Your Car', sub: 'Fair cash offer' },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-[#E8E2D6] group-hover:border-[#F5C842] transition-colors flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#D97706]`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#1A2332]">{item.label}</p>
                      <p className="text-[10px] text-[#6B7A8D]">{item.sub}</p>
                    </div>
                    <i className="ri-arrow-right-s-line text-[#6B7A8D] group-hover:text-[#D97706] transition-colors text-sm" />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Who You're Buying From */}
        <Link
          to="/about-the-owner"
          className="block rounded-2xl border border-[#E8E2D6] bg-[#FFFBF0] hover:bg-white hover:border-[#F5C842]/60 transition-all duration-200 group overflow-hidden shadow-sm"
          aria-label="Learn about the owner of Rockhound Auto"
        >
          <div className="bg-[#1A2332] px-4 py-2.5 flex items-center gap-2">
            <i className="ri-user-smile-line text-[#F5C842] text-sm" aria-hidden="true" />
            <span
              className="text-white text-[10px] font-black uppercase tracking-widest"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              WHO YOU'RE BUYING FROM
            </span>
          </div>
          <div className="px-4 py-4">
            <p className="text-xs text-[#4A5568] leading-relaxed mb-3">
              "I got tired of watching people get taken advantage of. So I built the dealership I always wished existed."
            </p>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-[#D97706] uppercase tracking-wider">Read the full story</span>
              <i className="ri-arrow-right-s-line text-[#D97706] group-hover:translate-x-0.5 transition-transform text-sm" aria-hidden="true" />
            </div>
          </div>
        </Link>

        {/* Charity strip */}
        <Link
          to="/community-impact"
          className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[#F0E8CC] bg-[#FFFBF0] hover:bg-[#FDF5E0] transition-colors group"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-[#F5C842] rounded-lg flex-shrink-0">
            <i className="ri-heart-line text-[#1E1A14] text-sm" />
          </div>
          <p className="text-xs font-semibold text-[#1A2332] flex-1">Every sale supports Oregon Dog Rescue</p>
          <i className="ri-arrow-right-s-line text-[#D97706] group-hover:translate-x-0.5 transition-transform text-sm" />
        </Link>
      </div>
    </div>
  );
}